import React, { useCallback, useMemo, useRef, useState } from "react";
import useProjectEditAccess from "@/services/context/project-process/use-project-edit-access";

import useProjectContext from "@/services/context/project/use-project-context.ts";
import useProjectIndicatorsContext from "@/pages/takeoff/context/project-indicators/use-project-indicators-context.ts";
import useViewerItemsVisibilityContext from "@/services/context/viewer/items-visibility/use-viewer-items-visibility-context";
import useProjectProcessContext from "@/services/context/project-process/use-project-process-context";
import useViewerContext from "@/services/context/viewer/instance/use-viewer-context";
import useDashboardProcess from "@/hooks/use-dashboard-process";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context";
import useProjectOrganisationContext from "@/pages/takeoff/context/project-organisation/use-project-organisation-context.ts";

import FlowMode from "@/models/enums/flow-mode.ts";
import ClosableDialog from "@/components/dialogs/closable-dialog";
import ValidationReview from "./validation-review-dialog-content.tsx";
import ScaleCardAction from "./actions/scale-card-action";
import DetectionCardAction from "./actions/detection-card-action";
import Calcul1PdfsimpleCardAction from "./actions/calcul-1-pdfsimple-card-action";
import Calcul1PdfsuperposeCardAction from "./actions/calcul-1-pdfsuperpose-card-action";
import Calcul1BimCardAction from "./actions/calcul-1-bim-card-action";
import Calcul2PdfsuperposeCardAction from "./actions/calcul-2-pdfsuperpose-card-action";
import Calcul3PdfsuperposeCardAction from "./actions/calcul-3-pdfsuperpose-card-action";
import useValidationContext from "@/pages/takeoff/layouts/validation/context/validation/use-validation-context";
import type { ProductionProcessOptions } from "@/services/context/project-process/project-process-runner";
import Button from "@/components/buttons/button/index.tsx";

interface MeasureCard3DViewerActionsProps {
    setupLaunch: (processName: string, deleteExistingMeasures: boolean, callbackOrOptions?: (() => Promise<void> | void) | ProductionProcessOptions) => void;
    onLaunchPlanDetection: () => void;
    onCalculateSurfaces: () => Promise<void>;
    onLaunchProdJob: () => Promise<boolean>;
}

const MeasureCard3DViewerActions: React.FC<MeasureCard3DViewerActionsProps> = ({
    setupLaunch,
    onLaunchPlanDetection,
    onCalculateSurfaces,
    onLaunchProdJob,
}) => {
    const { ensureCanEdit, canEdit } = useProjectEditAccess();
    const { project } = useProjectContext();
    const { storeysFileData } = useProjectMeasureContext();
    const { data: projectOrganisation } = useProjectOrganisationContext();
    const { isProcessRunning, isLoading: isProcessLoading, beginProcess } = useProjectProcessContext();
    const { validationPacks } = useValidationContext();
    const { dashboardProcessStatusQuery, updateDashboardProcessStatus } = useDashboardProcess(project.id);
    const { mutateAsync: updateProcessStatus } = updateDashboardProcessStatus;
    const { projectIndicatorsData } = useProjectIndicatorsContext();
    const { addItems, removeItems } = useViewerItemsVisibilityContext();
    const { isLoading: isViewerLoading } = useViewerContext();

    const actionTypeRef = useRef<'PDF Simple' | 'PDF Superposes - Calcul 1' | 'PDF Superposes - Calcul 2' | 'PDF Superposes - Calcul 3'>();

    const indicators = useMemo(() =>
        projectIndicatorsData?.buildings.flatMap(b => b.storeys.flatMap(s => s.indicators))
        , [projectIndicatorsData]);

    const onIndicatorCheckChanged = useCallback((indicatorKeyname: string, checked: boolean) => {
        if (indicators == null) return;

        const filteredIndicators = indicators.filter(i => i.def.keyName === indicatorKeyname);
        const ids = filteredIndicators?.map(i => i._3D);

        if (ids == null) return;
        if (checked) {
            addItems(ids);
        } else {
            removeItems(ids);
        }
    }, [indicators, addItems, removeItems]);

    const eligibleStoreys = useMemo(() =>
        projectOrganisation?.buildings.flatMap(b => b.storeys).filter(s =>
            s.storeyType !== 'TOIT' && (storeysFileData.get(s.id)?.length ?? 0) > 0
        ) ?? []
    , [projectOrganisation, storeysFileData]);

    const totalStoreysCount = eligibleStoreys.length;

    const scaledStoreysCount = useMemo(() =>
        eligibleStoreys.filter(s => {
            const files = storeysFileData.get(s.id);
            return files != null && files.length > 0 && files.every(f => f.content?.default === false);
        }).length
    , [eligibleStoreys, storeysFileData]);

    const storeysFileDataHasScale = useMemo(() =>
        scaledStoreysCount === totalStoreysCount
    , [totalStoreysCount, scaledStoreysCount]);

    const markCalculationDone = useCallback(async (revisionKey: 'revMetresDepuisPDF' | 'revMetresDepuisCalcul' | 'revCalcul1' | 'revCalcul2' | 'revCalcul3' | 'revSurfaces') => {
        await updateProcessStatus(current => {
            if (!current.revision) throw new Error("La révision du projet est introuvable.");
            return { [revisionKey]: current.revision };
        });
    }, [updateProcessStatus]);

    const [isBimDialogOpen, setIsBimDialogOpen] = useState<boolean>(false);
    const [isConfirmLaunchOpen, setIsConfirmLaunchOpen] = useState<boolean>(false);

    const isLoading = isProcessRunning || isProcessLoading || isViewerLoading;

    const handleOpenBimDialog = useCallback(() => {
        setIsBimDialogOpen(true);
    }, []);

    const handleLaunch = useCallback(async (deleteExistingMeasures: boolean) => {
        if (!ensureCanEdit()) {
            return;
        }

        if (isLoading || !actionTypeRef.current) return;
        const finishProcess = beginProcess();
        try {

            if (actionTypeRef.current === 'PDF Simple') {
                setupLaunch("Calcul PDF Simples", deleteExistingMeasures);
            } else if (actionTypeRef.current === 'PDF Superposes - Calcul 1') {
                setupLaunch("PDF Superposes - Calcul Package 1&2", deleteExistingMeasures);
            } else if (actionTypeRef.current === 'PDF Superposes - Calcul 2') {
                setupLaunch("PDF Superposes - Calcul Package 3", deleteExistingMeasures);
            } else if (actionTypeRef.current === 'PDF Superposes - Calcul 3') {
                setupLaunch("PDF Superposes - Calcul Package 4", deleteExistingMeasures);
            }

            if (actionTypeRef.current === 'PDF Simple' || actionTypeRef.current === 'PDF Superposes - Calcul 1') {
                try {
                    // Préparation des fichiers (UpdateStoreysWalls.json, UpdateStoreysOpenings.json, UpdateStoreysSpaces.json, UpdateStoreysSlabs.json) pour la suite des calculs
                    await onCalculateSurfaces();
                } catch {
                    // L'erreur est affichée par le calcul ; ne pas lancer le job.
                    return;
                }
            }

            await onLaunchProdJob();
        } finally {
            finishProcess();
        }
    }, [ensureCanEdit, isLoading, onCalculateSurfaces, onLaunchProdJob, setupLaunch, beginProcess]);

    const handleConfirmLaunch = useCallback(() => {
        setIsConfirmLaunchOpen(true);
    }, []);

    return (
        <>
            <div className='flex flex-col gap-y-2 pointer-events-none max-w-70 max-h-full overflow-y-auto'>
                {!storeysFileDataHasScale ? (
                    <ScaleCardAction
                        scaledStoreysCount={scaledStoreysCount}
                        totalStoreysCount={totalStoreysCount}
                        isLoading={isLoading}
                    />
                ) : (
                    <>
                        <DetectionCardAction
                            flowMode={project.flowMode}
                            storeysFileDataHasScale={storeysFileDataHasScale}
                            hasMetresDepuisPDF={dashboardProcessStatusQuery.data?.hasMetresDepuisPDF === true}
                            isLoading={isLoading}
                            onClick={() => {
                                setupLaunch("", true, async () => { await markCalculationDone('revMetresDepuisPDF') });
                                onLaunchPlanDetection();
                            }}
                        />

                        {project.flowMode === FlowMode._2D3D &&
                            <Calcul1PdfsimpleCardAction 
                                storeysFileDataHasScale={storeysFileDataHasScale}
                                hasMetresDepuisCalcul={dashboardProcessStatusQuery.data?.hasMetresDepuisCalcul === true}
                                isLoading={isLoading}
                                onClick={() => {
                                    actionTypeRef.current = 'PDF Simple';
                                    handleConfirmLaunch();
                                }}
                            />
                        }

                        {project.flowMode === FlowMode._2D3DAdvanced &&
                            <>
                                <div className='uppercase border-b border-gray-300 rounded text-gray-700 font-semibold pl-1.25 text-sm'>Avant projet</div>
                                <Calcul1PdfsuperposeCardAction 
                                    storeysFileDataHasScale={storeysFileDataHasScale}
                                    hasCalcul1={dashboardProcessStatusQuery.data?.hasCalcul1 === true}
                                    isLoading={isLoading}
                                    onClick={() => {
                                        actionTypeRef.current = 'PDF Superposes - Calcul 1';
                                        handleConfirmLaunch();
                                    }}
                                    onIndicatorCheckChanged={onIndicatorCheckChanged}
                                />
            
                                {dashboardProcessStatusQuery.data?.hasCalcul1 === true &&
                                    <>
                                        <Calcul2PdfsuperposeCardAction 
                                            storeysFileDataHasScale={storeysFileDataHasScale}
                                            hasCalcul2={dashboardProcessStatusQuery.data?.hasCalcul2 === true}
                                            validationPacks={validationPacks ?? []}
                                            isLoading={isLoading}
                                            onClick={() => {
                                                actionTypeRef.current = 'PDF Superposes - Calcul 2';
                                                handleConfirmLaunch();
                                            }}
                                            onIndicatorCheckChanged={onIndicatorCheckChanged}
                                        />

                                        <div className='uppercase border-b border-gray-300 rounded text-gray-700 font-semibold pl-1.25 text-sm'>Projet</div>
                                        <Calcul3PdfsuperposeCardAction 
                                            storeysFileDataHasScale={storeysFileDataHasScale}
                                            hasCalcul3={dashboardProcessStatusQuery.data?.hasCalcul3 === true}
                                            isLoading={isLoading}
                                            onClick={() => {
                                                actionTypeRef.current = 'PDF Superposes - Calcul 3';
                                                handleConfirmLaunch();
                                            }}
                                            onIndicatorCheckChanged={onIndicatorCheckChanged}
                                        />
                                    </>
                                }
                            </>
                        }

                    </>
                )}

                {project.flowMode === FlowMode._3D2D &&
                    <Calcul1BimCardAction
                        hasSurfaces={dashboardProcessStatusQuery.data?.hasSurfaces === true}
                        validationPacks={validationPacks ?? []}
                        isLoading={isLoading}
                        onClick={handleOpenBimDialog}
                    />
                }
            </div>

            <ClosableDialog
                open={isBimDialogOpen}
                onClose={() => setIsBimDialogOpen(false)}
                title='Finalisez le calcul'
            >
                <ValidationReview
                    onCancel={() => setIsBimDialogOpen(false)}
                    onValidate={(_selectedRevision, isNewRevision) => {
                        if (!ensureCanEdit()) {
                            return;
                        }
                        setIsBimDialogOpen(false);
                        setupLaunch(
                            "BAAMER :: Export : Promoter Surfaces (GOe)",
                            true,
                            { newBoomerRevision: isNewRevision }
                        );
                        onLaunchProdJob();
                    }}
                />
            </ClosableDialog>

            <ClosableDialog
                open={isConfirmLaunchOpen}
                onClose={() => setIsConfirmLaunchOpen(false)}
                title="Etes-vous sûr de lancer le calcul ?"
            >
                <div className="flex flex-row gap-x-2 items-center ml-auto">
                    <Button onClick={() => setIsConfirmLaunchOpen(false)} variant="ghost">
                        Annuler
                    </Button>
                    <Button disabled={!canEdit} onClick={() => {
                        handleLaunch(true);
                        setIsConfirmLaunchOpen(false);
                    }}>
                        Valider
                    </Button>
                </div>
            </ClosableDialog>
        </>
    );
};

export default MeasureCard3DViewerActions;
